import { useCallback, useState, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  MiniMap,
  Controls,
  Background,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { DOMAINS, DOMAIN_ORDER, NODE_DETAILS, type DomainId } from './archData';
import { buildGraph } from './layout';
import DetailPanel from './DetailPanel';
import DomainSidebar from './DomainSidebar';
import { countPending, syncFromFile } from './archQueue';

function ArchitecturePageInner() {
  const { fitView } = useReactFlow();
  const [collapsedDomains, setCollapsedDomains] = useState<Set<DomainId>>(new Set(DOMAIN_ORDER));
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(countPending());

  const { nodes, edges } = useMemo(() => buildGraph(collapsedDomains), [collapsedDomains]);

  // Masquer le footer statique de index.html pendant l'affichage de la map
  useEffect(() => {
    const footer = document.getElementById('static-footer');
    if (footer) footer.style.display = 'none';
    // Sync depuis arch-queue.json → localStorage (permet à Copilot de mettre à jour les statuts)
    syncFromFile();
    return () => { if (footer) footer.style.display = ''; };
  }, []);

  useEffect(() => {
    const handler = () => setPendingCount(countPending());
    window.addEventListener('arch_queue_update', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('arch_queue_update', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const toggleDomain = useCallback((domainId: DomainId) => {
    setCollapsedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domainId)) next.delete(domainId); else next.add(domainId);
      return next;
    });
  }, []);

  const selectDomain = useCallback((domainId: DomainId) => {
    setSelectedNodeId(prev => prev === domainId ? null : domainId);
    window.setTimeout(() => fitView({ nodes: [{ id: domainId }], duration: 400, padding: 0.3 }), 50);
  }, [fitView]);

  const focusNode = useCallback((nodeId: string, domainId: DomainId) => {
    setCollapsedDomains(prev => {
      const next = new Set(prev);
      next.delete(domainId);
      return next;
    });
    setSelectedNodeId(nodeId);
    window.setTimeout(() => fitView({ nodes: [{ id: nodeId }], duration: 400, padding: 0.8 }), 50);
  }, [fitView]);

  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    if (node.id in DOMAINS) {
      // Clic sur la carte = navigation (déplier/replier). Pour poser une
      // question au niveau domaine, utiliser la ligne dans la sidebar.
      toggleDomain(node.id as DomainId);
      return;
    }
    if (node.id in NODE_DETAILS) {
      setSelectedNodeId(prev => prev === node.id ? null : node.id);
    }
  }, [toggleDomain]);

  const handleClose = () => {
    // Retire le hash et retourne à l'app
    window.history.pushState('', document.title, window.location.pathname + window.location.search);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: '#0f172a',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 9999,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        background: '#1e293b',
        borderBottom: '1px solid #334155',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🗺️</span>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f1f5f9', fontFamily: 'system-ui' }}>
                Chord Notator — Architecture Explorer
              </h1>
              {pendingCount > 0 && (
                <span style={{
                  background: '#f59e0b',
                  color: '#0f172a',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '2px 10px',
                  borderRadius: 10,
                  whiteSpace: 'nowrap',
                }}>
                  ● {pendingCount} message{pendingCount > 1 ? 's' : ''} en attente
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b', fontFamily: 'system-ui' }}>
              Carte : cliquez un domaine pour déplier/replier, un composant pour le cibler précisément · Sidebar : cliquez le nom d'un domaine pour poser une question à Copilot
            </p>
          </div>
        </div>

        {/* Bouton fermer */}
        <button
          onClick={handleClose}
          style={{
            background: '#334155',
            border: '1px solid #475569',
            color: '#f1f5f9',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'system-ui',
            flexShrink: 0,
          }}
        >
          ← Retour à l'app
        </button>
      </div>

      {/* Corps : sidebar + canvas */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <DomainSidebar
          collapsedDomains={collapsedDomains}
          onToggleDomain={toggleDomain}
          onSelectDomain={selectDomain}
          onFocusNode={focusNode}
        />

        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodeClick={onNodeClick}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.1}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={false}
          >
            <MiniMap
              style={{ background: '#1e293b', border: '1px solid #334155' }}
              nodeColor={(node) => {
                const domainId = (NODE_DETAILS[node.id]?.domain ?? node.id) as DomainId;
                return DOMAINS[domainId]?.bg ?? '#475569';
              }}
            />
            <Controls style={{ background: '#1e293b', border: '1px solid #334155' }} />
            <Background color="#334155" gap={24} size={1} />
          </ReactFlow>

          {/* Panneau détail */}
          <DetailPanel
            nodeId={selectedNodeId}
            onClose={() => setSelectedNodeId(null)}
          />
        </div>
      </div>
    </div>
  );
}

export default function ArchitecturePage() {
  return (
    <ReactFlowProvider>
      <ArchitecturePageInner />
    </ReactFlowProvider>
  );
}

